import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import NewsItem from './NewsItem';
import { trackNewsEvent } from '../utils/newsEvents';

const POPULAR_WINDOW = 'week';
const POPULAR_LIMIT_PER_REGION = 5;
const NEWS_PAGE_LIMIT = 50;
const VALID_FILTERS = ['finnish', 'international', 'all', 'popular'];

const getInitialFilter = () => {
  if (typeof window === 'undefined') {
    return 'finnish';
  }

  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');
  const region = params.get('region');

  if (view === 'popular') {
    return 'popular';
  }

  if (VALID_FILTERS.includes(region)) {
    return region;
  }

  return 'finnish';
};

const getInitialSearchQuery = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return new URLSearchParams(window.location.search).get('q') || '';
};

const NewsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.border};
  background-color: ${({ theme }) => theme.bgSecondary};
`;

const NewsListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${({ theme }) => theme.border};
`;

const DesktopLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const DesktopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 260px;
  column-gap: 1rem;
  align-items: start;
`;

const DesktopControls = styled.div`
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
`;

const DesktopControlsSpacer = styled.div`
  grid-column: 2;
  grid-row: 1;
`;

const DesktopNews = styled.div`
  grid-column: 1;
  grid-row: 2;
  min-width: 0;
`;

const DesktopPopular = styled.div`
  grid-column: 2;
  grid-row: 2;
`;

const PopularColumn = styled.aside`
  width: 260px;
  flex: 0 0 260px;
`;

const PopularCard = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  background-color: ${({ theme }) => theme.bgSecondary};
`;

const PopularHeader = styled.div`
  padding: 0.9rem 0.9rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const PopularTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
`;

const PopularSubtitle = styled.div`
  margin-top: 0.15rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

const PopularSection = styled.div`
  padding: 0.75rem 0.9rem 0.25rem;
`;

const PopularSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 0.5rem;
`;

const PopularItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PopularMessage = styled.div`
  padding: 0.75rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

const PopularItemContainer = styled.a`
  padding: 0.55rem 0;
  border-top: 1px solid ${({ theme }) => theme.border};
  transition: background-color 0.2s ease;
  display: block;
  text-decoration: none;
  color: ${({ theme }) => theme.text};

  &:hover {
    background-color: ${({ theme }) => theme.hover};
  }
`;

const PopularItemTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
`;

const PopularItemMeta = styled.div`
  margin-top: 0.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

const PopularItemSource = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.accent};
  font-size: 0.62rem;
`;

const PopularItemTime = styled.span`
  opacity: 0.85;
  font-size: 0.62rem;
  white-space: nowrap;
`;

const NewsListTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text};
`;

const NewsCount = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.border};
`;

const FilterSearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: ${({ theme }) => theme.bg};
    padding-top: 1rem;
    padding-bottom: 1rem;
    margin-top: -1.25rem;
    left: 0;
    right: 0;
    margin-left: calc((100vw - 100%) / -2);
    margin-right: calc((100vw - 100%) / -2);
    padding-left: calc((100vw - 100%) / 2);
    padding-right: calc((100vw - 100%) / 2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 0.35rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0.5rem;
  width: 310px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  padding-right: ${({ $hasValue }) => $hasValue ? '2.5rem' : '1rem'};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: ${({ theme }) => theme.bgSecondary};
  color: ${({ theme }) => theme.text};
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${({ theme }) => theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.accent}20;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
  
  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    padding-right: ${({ $hasValue }) => $hasValue ? '2.25rem' : '0.85rem'};
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    padding-right: ${({ $hasValue }) => $hasValue ? '2rem' : '0.75rem'};
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.6;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  min-width: 24px;
  min-height: 24px;
  
  &:hover {
    opacity: 1;
    background-color: ${({ theme }) => theme.hover};
    color: ${({ theme }) => theme.text};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus {
    outline: none;
    opacity: 1;
    background-color: ${({ theme }) => theme.hover};
  }
  
  @media (max-width: 768px) {
    right: 0.4rem;
    min-width: 22px;
    min-height: 22px;
  }
  
  @media (max-width: 480px) {
    right: 0.35rem;
    min-width: 20px;
    min-height: 20px;
  }
`;

const ClearIcon = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SearchButton = styled.button`
  padding: 0.5rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.bg};
  min-height: 44px;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    min-height: 40px;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const FilterButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active' && prop !== '$active',
})`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ $active, theme }) => 
    $active ? theme.accent : 'transparent'};
  color: ${({ $active, theme }) => 
    $active ? theme.bg : theme.text};
  min-height: 44px; /* Touch-friendly minimum size */
  
  &:hover {
    background-color: ${({ $active, theme }) => 
      $active ? theme.accent : theme.hover};
  }
  
  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    min-height: 40px;
    flex: 1;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid ${({ theme }) => theme.border};
  background-color: ${({ theme }) => theme.bgSecondary};
`;

const LoadingSpinner = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  border: 2px solid ${({ theme }) => theme.border};
  border-left-color: ${({ theme }) => theme.accent};
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #dc3545;
  background-color: ${({ theme }) => theme.bgSecondary};
  border: 1px solid #dc3545;
  margin: 1rem 0;
`;

const UpdateInfoBox = styled.div`
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.accent}20;
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.accent}30;
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 0.85rem;
    font-size: 0.85rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const UpdateInfoText = styled.span`
  flex: 1;
`;

const UpdateButton = styled.button`
  padding: 0.4rem 1rem;
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.bg};
  white-space: nowrap;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.5rem;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
`;

const LoadMoreButton = styled.button`
  padding: 0.75rem 2rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${({ $disabled, theme }) => 
    $disabled ? theme.bgSecondary : theme.accent};
  color: ${({ $disabled, theme }) => 
    $disabled ? theme.textSecondary : theme.bg};
  min-height: 44px;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 1.75rem;
    font-size: 0.85rem;
    min-height: 40px;
    width: 100%;
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const ButtonSpinner = styled(LoadingSpinner)`
  width: 1rem;
  height: 1rem;
  border-width: 2px;
  flex-shrink: 0;
`;

const EndMessage = styled.div`
  text-align: center;
  padding: 1rem 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
`;

const NewsList = () => {
  const [filter, setFilter] = useState(getInitialFilter);
  const [searchQuery, setSearchQuery] = useState(getInitialSearchQuery);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(getInitialSearchQuery);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateFailed, setUpdateFailed] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [popularFinnish, setPopularFinnish] = useState([]);
  const [popularInternational, setPopularInternational] = useState([]);
  const [popularLoading, setPopularLoading] = useState(true);
  const [popularError, setPopularError] = useState(null);
  const hasLoadedNewsRef = useRef(false);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  });

  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }

    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = (event) => setIsMobile(event.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
    } else {
      mql.addListener(onChange);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, []);

  useEffect(() => {
    // Keep filter valid across responsive modes.
    if (isMobile && filter === 'all') {
      setFilter('popular');
    }
    if (!isMobile && filter === 'popular') {
      setFilter('all');
    }
  }, [isMobile, filter]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (filter === 'popular') {
      params.set('view', 'popular');
      params.delete('region');
    } else {
      params.delete('view');
      if (filter === 'finnish') {
        params.delete('region');
      } else {
        params.set('region', filter);
      }
    }

    const trimmedSearch = appliedSearchQuery.trim();
    if (trimmedSearch) {
      params.set('q', trimmedSearch);
    } else {
      params.delete('q');
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [appliedSearchQuery, filter]);

  const buildNewsUrl = useCallback((cursor = null) => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const params = new URLSearchParams();

    params.set('limit', String(NEWS_PAGE_LIMIT));

    if (filter !== 'all' && filter !== 'popular') {
      params.set('region', filter);
    }

    const trimmedSearch = appliedSearchQuery.trim();
    if (trimmedSearch) {
      params.set('q', trimmedSearch);
    }

    if (cursor) {
      params.set('cursor', cursor);
    }

    return `${apiUrl}/api/news?${params.toString()}`;
  }, [appliedSearchQuery, filter]);

  const fetchNews = useCallback(async ({
    showLoading = false,
    clearOnError = false,
    append = false,
    cursor = null,
  } = {}) => {
    if (filter === 'popular') {
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setUpdateFailed(false);
      setError(null);

      const response = await fetch(buildNewsUrl(cursor));

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.items)) {
        throw new Error('Invalid data format from API');
      }

      setNews((previousNews) => (
        append ? [...previousNews, ...data.items] : data.items
      ));
      setNextCursor(data.next_cursor || null);
      setHasMorePages(Boolean(data.has_more));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.message);

      if (clearOnError && showLoading) {
        setNews([]);
        setLoading(false);
      } else {
        setUpdateFailed(true);
        if (showLoading) {
          setLoading(false);
        }
      }
    }
  }, [buildNewsUrl, filter]);

  const fetchPopular = useCallback(async () => {
    try {
      setPopularError(null);
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const buildPopularUrl = (region) => {
        const params = new URLSearchParams({
          window: POPULAR_WINDOW,
          limit: String(POPULAR_LIMIT_PER_REGION),
          region,
        });
        return `${apiUrl}/api/news/popular?${params.toString()}`;
      };

      const [finnishResponse, internationalResponse] = await Promise.all([
        fetch(buildPopularUrl('finnish')),
        fetch(buildPopularUrl('international')),
      ]);

      if (!finnishResponse.ok) {
        throw new Error(`API request failed: ${finnishResponse.status}`);
      }
      if (!internationalResponse.ok) {
        throw new Error(`API request failed: ${internationalResponse.status}`);
      }

      const [finnishData, internationalData] = await Promise.all([
        finnishResponse.json(),
        internationalResponse.json(),
      ]);

      setPopularFinnish(Array.isArray(finnishData.items) ? finnishData.items : []);
      setPopularInternational(Array.isArray(internationalData.items) ? internationalData.items : []);
      setPopularLoading(false);
    } catch (err) {
      console.error('Error fetching popular news:', err);
      setPopularError(err.message);
      setPopularLoading(false);
    }
  }, []);

  useEffect(() => {
    const showInitialLoading = !hasLoadedNewsRef.current;
    hasLoadedNewsRef.current = true;
    fetchNews({ showLoading: showInitialLoading, clearOnError: showInitialLoading });
  }, [fetchNews]);

  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchNews();
      fetchPopular();
    }, 15 * 60 * 1000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNews();
        fetchPopular();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchNews, fetchPopular]);

  const loadMore = async () => {
    if (loadingMore || !hasMorePages || !nextCursor) {
      return;
    }

    try {
      setLoadingMore(true);
      await fetchNews({ append: true, cursor: nextCursor });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRetryUpdate = async () => {
    if (retrying) return;

    setRetrying(true);
    setUpdateFailed(false);

    try {
      await fetchNews({ showLoading: false, clearOnError: false });
      await fetchPopular();
    } finally {
      setRetrying(false);
    }
  };

  const applySearch = () => {
    const nextQuery = searchQuery.trim();
    if (nextQuery === appliedSearchQuery.trim()) {
      return;
    }

    setNextCursor(null);
    setHasMorePages(true);
    setAppliedSearchQuery(nextQuery);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    applySearch();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (appliedSearchQuery) {
      setNextCursor(null);
      setHasMorePages(true);
      setAppliedSearchQuery('');
    }
  };

  if (loading && filter !== 'popular') {
    return (
      <LoadingMessage>
        <LoadingSpinner />
        Ladataan uutisia...
      </LoadingMessage>
    );
  }

  // Only show error message if we have no news at all (initial load failed)
  if (error && news.length === 0 && filter !== 'popular') {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  const handleFilterChange = (newFilter) => {
    if (newFilter !== filter) {
      setSearchQuery('');
      setAppliedSearchQuery('');
      setNextCursor(null);
      setHasMorePages(true);
    }
    setFilter(newFilter);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const PopularItem = ({ news: popular }) => {
    const handleClick = (event) => {
      if (event.button !== 0) {
        return;
      }
      trackNewsEvent(popular.guid, 'open');
    };

    const handleAuxClick = (event) => {
      if (event.button !== 1) {
        return;
      }
      trackNewsEvent(popular.guid, 'open');
    };

    return (
      <PopularItemContainer
        href={popular.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onAuxClick={handleAuxClick}
      >
        <PopularItemTitle>{popular.title}</PopularItemTitle>
        <PopularItemMeta>
          <PopularItemSource>{popular.source}</PopularItemSource>
          <PopularItemTime>{formatDate(popular.publish_time)}</PopularItemTime>
        </PopularItemMeta>
      </PopularItemContainer>
    );
  };

  const filterButtons = isMobile
    ? [
        { key: 'finnish', label: 'Suomesta' },
        { key: 'international', label: 'Maailmalta' },
        { key: 'popular', label: 'Suosituimmat' },
      ]
    : [
        { key: 'finnish', label: 'Suomesta' },
        { key: 'international', label: 'Maailmalta' },
        { key: 'all', label: 'Kaikki' },
      ];

  const mainTitle = filter === 'popular' ? 'Suosituimmat uutiset' : 'Uusimmat uutiset';
  const mainCount = filter === 'popular'
    ? (popularFinnish.length + popularInternational.length)
    : news.length;

  const renderPopularList = (items) => {
    if (popularLoading) {
      return <PopularMessage>Ladataan suosituimpia...</PopularMessage>;
    }
    if (popularError) {
      return <PopularMessage>{popularError}</PopularMessage>;
    }
    if (!items || items.length === 0) {
      return <PopularMessage>Ei suosittuja uutisia.</PopularMessage>;
    }
    return (
      <PopularItems>
        {items.map((item) => (
          <PopularItem key={item.id} news={item} />
        ))}
      </PopularItems>
    );
  };

  return (
    <>
      {updateFailed && (
        <UpdateInfoBox onClick={handleRetryUpdate}>
          <UpdateInfoText>
            Uutisten päivitys epäonnistui. Klikkaa päivittääksesi uutiset.
          </UpdateInfoText>
          <UpdateButton disabled={retrying}>
            {retrying ? 'Päivitetään...' : 'Päivitä'}
          </UpdateButton>
        </UpdateInfoBox>
      )}
      {isMobile ? (
        <>
          <NewsListHeader>
            <NewsListTitle>{mainTitle}</NewsListTitle>
            {mainCount > 0 && <NewsCount>{mainCount} uutista</NewsCount>}
          </NewsListHeader>
          <FilterSearchContainer>
            <FilterContainer>
              {filterButtons.map((btn) => (
                <FilterButton
                  key={btn.key}
                  $active={filter === btn.key}
                  onClick={() => handleFilterChange(btn.key)}
                >
                  {btn.label}
                </FilterButton>
              ))}
            </FilterContainer>
            {filter !== 'popular' && (
              <SearchForm onSubmit={handleSearchSubmit}>
                <SearchInputWrapper>
                  <SearchInput
                    type="text"
                    placeholder="Etsi uutisista..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    $hasValue={searchQuery.length > 0}
                  />
                  {searchQuery.length > 0 && (
                    <ClearButton
                      type="button"
                      onClick={handleClearSearch}
                      aria-label="Tyhjennä haku"
                      title="Tyhjennä haku"
                    >
                      <ClearIcon>×</ClearIcon>
                    </ClearButton>
                  )}
                </SearchInputWrapper>
                <SearchButton type="submit">Hae</SearchButton>
              </SearchForm>
            )}
          </FilterSearchContainer>
          {filter === 'popular' ? (
            <>
              <NewsListContainer>
                <PopularSection>
                  <PopularSectionTitle>Suomesta</PopularSectionTitle>
                  {renderPopularList(popularFinnish)}
                </PopularSection>
                <PopularSection>
                  <PopularSectionTitle>Maailmalta</PopularSectionTitle>
                  {renderPopularList(popularInternational)}
                </PopularSection>
              </NewsListContainer>
            </>
          ) : (
            <>
              <NewsListContainer>
                {news.map(item => (
                  <NewsItem key={item.id} news={item} />
                ))}
              </NewsListContainer>
              <LoadMoreContainer>
                {hasMorePages ? (
                  <LoadMoreButton
                    onClick={loadMore}
                    disabled={loadingMore}
                    $disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <ButtonSpinner />
                        Ladataan...
                      </>
                    ) : (
                      'Lataa lisää'
                    )}
                  </LoadMoreButton>
                ) : (
                  <EndMessage>
                    Kaikki uutiset on ladattu.
                  </EndMessage>
                )}
              </LoadMoreContainer>
            </>
          )}
        </>
      ) : (
        <DesktopLayout>
          <DesktopGrid>
            <DesktopControls>
              <NewsListHeader>
                <NewsListTitle>Uusimmat uutiset</NewsListTitle>
                {news.length > 0 && <NewsCount>{news.length} uutista</NewsCount>}
              </NewsListHeader>
              <FilterSearchContainer>
                <FilterContainer>
                  {filterButtons.map((btn) => (
                    <FilterButton
                      key={btn.key}
                      $active={filter === btn.key}
                      onClick={() => handleFilterChange(btn.key)}
                    >
                      {btn.label}
                    </FilterButton>
                  ))}
                </FilterContainer>
                <SearchForm onSubmit={handleSearchSubmit}>
                  <SearchInputWrapper>
                    <SearchInput
                      type="text"
                      placeholder="Etsi uutisista..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      $hasValue={searchQuery.length > 0}
                    />
                    {searchQuery.length > 0 && (
                      <ClearButton
                        type="button"
                        onClick={handleClearSearch}
                        aria-label="Tyhjennä haku"
                        title="Tyhjennä haku"
                      >
                        <ClearIcon>×</ClearIcon>
                      </ClearButton>
                    )}
                  </SearchInputWrapper>
                  <SearchButton type="submit">Hae</SearchButton>
                </SearchForm>
              </FilterSearchContainer>
            </DesktopControls>
            <DesktopControlsSpacer />
            <DesktopNews>
              <NewsListContainer>
                {news.map(item => (
                  <NewsItem key={item.id} news={item} />
                ))}
              </NewsListContainer>
              <LoadMoreContainer>
                {hasMorePages ? (
                  <LoadMoreButton
                    onClick={loadMore}
                    disabled={loadingMore}
                    $disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <ButtonSpinner />
                        Ladataan...
                      </>
                    ) : (
                      'Lataa lisää'
                    )}
                  </LoadMoreButton>
                ) : (
                  <EndMessage>
                    Kaikki uutiset on ladattu.
                  </EndMessage>
                )}
              </LoadMoreContainer>
            </DesktopNews>
            <DesktopPopular>
              <PopularColumn>
              <PopularCard>
                <PopularHeader>
                  <PopularTitle>Suosituimmat</PopularTitle>
                  <PopularSubtitle>Viikko</PopularSubtitle>
                </PopularHeader>
                {filter === 'all' ? (
                  <>
                    <PopularSection>
                      <PopularSectionTitle>Suomesta</PopularSectionTitle>
                      {renderPopularList(popularFinnish)}
                    </PopularSection>
                    <PopularSection>
                      <PopularSectionTitle>Maailmalta</PopularSectionTitle>
                      {renderPopularList(popularInternational)}
                    </PopularSection>
                  </>
                ) : (
                  <PopularSection>
                    <PopularSectionTitle>
                      {filter === 'finnish' ? 'Suomesta' : 'Maailmalta'}
                    </PopularSectionTitle>
                    {renderPopularList(
                        filter === 'finnish' ? popularFinnish : popularInternational
                      )}
                  </PopularSection>
                )}
              </PopularCard>
              </PopularColumn>
            </DesktopPopular>
          </DesktopGrid>
        </DesktopLayout>
      )}
    </>
  );
};

export default NewsList;
